import { ReconnectResponse, SessionInfo } from './types.js';
import { config } from '../config/environment.js';
import { retryWithBackoff } from '../utils/retry.js';
import { getReconnectSecret, getChatId, setChatId, setReconnectSecret } from './session-storage.js';

const DEFAULT_TIMEOUT_MS = 10000; // 10 seconds

export class SessionManager {
  private session: SessionInfo | null = null;
  private lastReconnectMessages: ReconnectResponse['messages'] = [];
  private lastReconnectChatCreatedAt: string | undefined;

  getReconnectMessages(): ReconnectResponse['messages'] {
    return this.lastReconnectMessages;
  }

  getReconnectChatCreatedAt(): string | undefined {
    return this.lastReconnectChatCreatedAt;
  }

  /**
   * Hydrates session from sessionStorage (reconnectSecret + chatId).
   * sessionKey is left empty — it will be obtained via BFF reconnect.
   * Returns true if both identifiers were found.
   */
  hydrateFromStorage(): boolean {
    const reconnectSecret = getReconnectSecret();
    const chatId = getChatId();
    if (reconnectSecret && chatId) {
      this.session = { reconnectSecret, chatId, sessionKey: '' };
      return true;
    }
    return false;
  }

  getSession(): SessionInfo | null {
    return this.session;
  }

  async createSession(source: string): Promise<{ wsUrl: string }> {
    const sessionsUrl = config.SESSIONS_URL;

    return retryWithBackoff(
      () => this.attemptCreateSession(sessionsUrl, source),
      {
        label: 'SessionManager',
        isRetriable: (error) => this.isRetriableError(error),
      }
    );
  }

  private async attemptCreateSession(
    sessionsUrl: string,
    source: string
  ): Promise<{ wsUrl: string }> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);

    try {
      const resp = await fetch(sessionsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source }),
        signal: controller.signal,
      });

      if (!resp.ok) {
        const errorMsg = await resp.text().catch(() => '');
        throw new Error(
          `BFF returned ${resp.status} ${resp.statusText}${errorMsg ? ': ' + errorMsg.substring(0, 100) : ''}`
        );
      }

      const body = await resp.json();

      if (!body?.chatId || !body?.sessionKey || !body?.reconnectSecret) {
        throw new Error('Invalid session response from BFF - missing required fields');
      }

      this.session = { chatId: body.chatId, reconnectSecret: body.reconnectSecret, sessionKey: body.sessionKey };
      setReconnectSecret(body.reconnectSecret);
      setChatId(body.chatId);

      // Construct WebSocket URL from BFF base URL (chatId + sessionKey only)
      const wsUrl = this.buildWsUrl(body.chatId, body.sessionKey);

      return { wsUrl };
    } catch (error) {
      // Handle timeout specifically
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new Error('Connection timeout - BFF took too long to respond');
      }

      // Re-throw other errors
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  private isRetriableError(error: Error): boolean {
    const message = error.message.toLowerCase();

    // Retriable errors
    if (
      message.includes('timeout') ||
      message.includes('connection refused') ||
      message.includes('econnrefused') ||
      message.includes('503') || // Service unavailable
      message.includes('502') || // Bad gateway
      message.includes('504') // Gateway timeout
    ) {
      return true;
    }

    // Non-retriable errors
    if (
      message.includes('401') || // Unauthorized
      message.includes('403') || // Forbidden
      message.includes('404') || // Not found
      message.includes('invalid') || // Invalid request/response
      message.includes('missing required fields')
    ) {
      return false;
    }

    // Default: retriable for network-like errors
    return message.includes('network') || message.includes('connection');
  }

  /**
   * Reconnects an existing session via BFF POST /chat/reconnect.
    * Sends only identifiers (reconnectSecret, chatId) — never tokens.
   * Returns the new wsUrl on success.
   */
  async reconnectSession(): Promise<{ wsUrl: string }> {
    if (!this.session) {
      throw new Error('No existing session to reconnect');
    }

    const reconnectUrl = config.RECONNECT_URL;

    return retryWithBackoff(
      () => this.attemptReconnectSession(reconnectUrl),
      {
        label: 'SessionManager:reconnect',
        // Retry all errors except auth failures (401/403) — user expects 3 full attempts
        isRetriable: (error) => {
          const msg = error.message.toLowerCase();
          return !(msg.includes('401') || msg.includes('403'));
        },
      }
    );
  }

  private async attemptReconnectSession(reconnectUrl: string): Promise<{ wsUrl: string }> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);

    try {
      const { chatId, reconnectSecret } = this.session!;

      const resp = await fetch(reconnectUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reconnectSecret,
          chatId,
        }),
        signal: controller.signal,
      });

      if (!resp.ok) {
        const errorMsg = await resp.text().catch(() => '');
        throw new Error(
          `BFF reconnect returned ${resp.status} ${resp.statusText}${errorMsg ? ': ' + errorMsg.substring(0, 100) : ''}`
        );
      }

      const body: ReconnectResponse = await resp.json();

      if (!body) {
        throw new Error('BFF reconnect returned connected: false');
      }

      // Update session state if BFF returns new values
      if (body.sessionKey) {
        this.session!.sessionKey = body.sessionKey;
      }
      if (body.chatId) {
        this.session!.chatId = body.chatId;
        setChatId(body.chatId);
      }

      // Use wsUrl from response if provided, otherwise build from current session
      const wsUrl = body.wsUrl || this.buildWsUrl(this.session!.chatId, this.session!.sessionKey);

      this.lastReconnectMessages = body.messages ?? [];
      this.lastReconnectChatCreatedAt =
        body.chatCreatedAt ??
        body.messages?.[0]?.chatCreatedAt;

      return { wsUrl };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new Error('Reconnect timeout - BFF took too long to respond');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Builds a WebSocket URL from the BFF base URL and session identifiers.
   */
  private buildWsUrl(chatId: string, sessionKey: string): string {
    const baseUrl = config.BFF_URL;
    const bffWsBase = baseUrl
      .replace('http://', 'ws://')
      .replace('https://', 'wss://')
      .replace(/\/api$/, '');

    return `${bffWsBase}/ws/chat?chatid=${encodeURIComponent(chatId)}&sessionkey=${encodeURIComponent(sessionKey)}`;
  }
}

export const sessionManager = new SessionManager();
export default SessionManager;
